// ============================================================================
// COMPANY SCOPE
// ----------------------------------------------------------------------------
// A driver (or truck) belongs to the parent company they are in RIGHT NOW plus
// every parent company they have EVER been in.
//
// Why: `parentCompanyId` holds a single value, so moving a driver from Company A
// to Company B used to erase A's link entirely. A's Main Admin then lost the
// driver from every dropdown and filter — their past loads still existed but
// showed "N/A" for the driver and could not be put on a settlement statement.
// Company history fixes that without exposing B's work to A: loads stay scoped
// by the load's own parentCompanyId, which is stamped once at creation.
//
// `companyHistoryIds` is append-only. Nothing here ever removes an entry.
// ============================================================================

export const COMPANY_HISTORY_FIELD = 'companyHistoryIds';

/**
 * Every parent company this record has ever belonged to.
 *
 * Falls back to the current `parentCompanyId` for records written before the
 * history field existed, so this behaves correctly both before and after the
 * backfill has run. That fallback is what makes this safe to deploy on its own.
 */
/**
 * Teams a user belongs to. Defined here rather than imported from teams.js so
 * the scoping rules stay a leaf module with no dependencies of their own.
 */
//
// Same rule as getUserTeamIds in teams.js: a team only counts when its company
// (the part of the id before "_team_") is on the user's company list.
function viewerTeamIdsOf(user) {
  const ids = Array.isArray(user?.teamIds) ? user.teamIds.filter(Boolean) : [];
  const companies = user?.assignedParentCompanyIds;
  if (!Array.isArray(companies)) return ids;
  return ids.filter(t => {
    const at = typeof t === 'string' ? t.indexOf('_team_') : -1;
    return at <= 0 || companies.includes(t.slice(0, at));
  });
}

export function getCompanyScopeIds(record) {
  if (!record) return [];

  const history = record[COMPANY_HISTORY_FIELD];
  if (Array.isArray(history) && history.length > 0) {
    return history.filter(Boolean);
  }

  return record.parentCompanyId ? [record.parentCompanyId] : [];
}

/**
 * True when the record belongs to one of the viewer's companies RIGHT NOW.
 *
 * This deliberately ignores companyHistoryIds. Visibility used to include every
 * company a record had ever belonged to, which meant access only ever grew: a
 * driver who moved stayed visible to their old company forever, and so did
 * every dispatcher who had ever worked with them. 50 of 185 drivers ended up
 * visible to companies they had left, and nobody could work out who was
 * supposed to see what.
 *
 * The past is still served, just not here:
 *   • Loads keep their own parentCompanyId, so whoever ran a job keeps seeing
 *     that job for accounting, forever.
 *   • Continued access to a person is an explicit, expiring manual grant a
 *     Super Admin issues — visible, deliberate and revocable.
 *
 * History is still recorded (see withCompanyHistory) because the Rebuild tool
 * and reporting use it. It simply no longer grants sight of anyone.
 */
export function isVisibleToCompanies(record, viewerCompanyIds) {
  if (!Array.isArray(viewerCompanyIds) || viewerCompanyIds.length === 0) return false;

  const current = record?.parentCompanyId;
  return Boolean(current) && viewerCompanyIds.includes(current);
}

/**
 * A dispatcher with no admin hat.
 *
 * Their driver lists are narrowed to the drivers linked to them, so one
 * dispatcher cannot pick another's driver by mistake. Admins keep the full
 * list — covering for dispatchers is part of that job.
 */
export function isPlainDispatcher(user) {
  const roles = Array.isArray(user?.role) ? user.role : [user?.role].filter(Boolean);
  return roles.includes('Dispatcher') &&
    !roles.some(r => ['Super Admin', 'Main Admin', 'Admin', 'HR', 'Fleet'].includes(r));
}

/**
 * Narrow a driver list to the ones this dispatcher is responsible for.
 *
 * Returns the list unchanged for admins, and also while a dispatcher has no
 * drivers linked yet — an empty list would stop them working entirely, which is
 * worse than showing too many. Once a Main Admin links their first driver, the
 * narrowing takes effect on its own.
 */
export function limitToLinkedDrivers(driverList, user, grantedDriverIds = null) {
  if (!Array.isArray(driverList) || !isPlainDispatcher(user)) return driverList;

  // The signed-in user object is built as { uid, email, ...userDoc }, so it
  // carries `uid` and no `id` — while a user read straight out of the users
  // collection carries `id`. Accept either: matching on the wrong one silently
  // finds nothing, and the fallback below then shows the dispatcher EVERY
  // driver, which is the opposite of what this function is for.
  const viewerId = user?.id || user?.uid || null;
  if (!viewerId) return driverList;

  const granted = new Set(grantedDriverIds || getGrantedDriverIds(user));

  // The team box is the rule; a Super Admin's manual grant is the exception
  // that adds to it. A granted driver is theirs to work with even though no
  // team links them.
  // Exactly the drivers linked to this dispatcher right now, plus anyone a
  // Super Admin granted. Nothing historical: a driver who used to be theirs, or
  // who shares their team but answers to someone else, is not theirs.
  //
  // No fallback. An earlier version returned the full list when a dispatcher had
  // nobody linked, meaning to avoid locking them out — but "no drivers yet" and
  // "every driver in the company" are opposite answers, and it silently chose
  // the widest one at the moment narrowing mattered most. An empty list is
  // honest, and the team box now warns loudly when a driver has no dispatcher.
  // Only narrow where this feature is actually in use.
  //
  // If no driver in this list has been linked to ANY dispatcher, the company
  // has not adopted driver-to-dispatcher assignment and everyone should carry
  // on seeing what they saw before. Without this check, turning the rule on
  // would blind every dispatcher in every company that has not set it up —
  // they would open the app to an empty list and no explanation.
  //
  // Note this is per-company adoption, not a per-person let-off: once ANY
  // driver here is linked, a dispatcher with nobody assigned correctly sees
  // nobody, and the team box shows that gap in red.
  const featureInUse = driverList.some(d => d.dispatcherId);
  if (!featureInUse) return driverList;

  return driverList.filter(d => d.dispatcherId === viewerId || granted.has(d.id));
}

/**
 * The history array to write when assigning `parentCompanyId` to a record.
 *
 * Seeds from the record's existing company on first write, so a driver who has
 * never been touched since the backfill still keeps their original company when
 * they get moved. Never drops an existing entry.
 */
export function withCompanyHistory(record, parentCompanyId) {
  const existing = Array.isArray(record?.[COMPANY_HISTORY_FIELD])
    ? record[COMPANY_HISTORY_FIELD].filter(Boolean)
    : [];

  const seeded = existing.length === 0 && record?.parentCompanyId
    ? [record.parentCompanyId]
    : existing;

  if (!parentCompanyId || seeded.includes(parentCompanyId)) return seeded;

  return [...seeded, parentCompanyId];
}

// ============================================================================
// MANUAL DRIVER ACCESS
// ----------------------------------------------------------------------------
// A Super Admin can hand a specific user visibility of a specific driver who
// belongs to a company that user doesn't manage — optionally with an expiry.
// This is the deliberate escape hatch for "this admin needs to keep seeing this
// driver's work after a transfer", without widening company assignments.
//
// Stored on the USER document as `manualDriverAccess`:
//   [{ driverId, driverName, grantedAt, grantedBy, grantedByEmail, expiresAt }]
// `expiresAt: null` means it never expires.
// ============================================================================

export const MANUAL_DRIVER_ACCESS_FIELD = 'manualDriverAccess';

/** Coerce a Firestore Timestamp, Date or ISO string to a Date. */
function toDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** True if a grant is still in force right now. */
export function isGrantActive(grant, now = new Date()) {
  if (!grant?.driverId) return false;
  if (!grant.expiresAt) return true; // permanent
  const expires = toDate(grant.expiresAt);
  return expires ? expires.getTime() > now.getTime() : true;
}

/** Every grant on this user, active and expired, newest first. */
export function getDriverGrants(user) {
  const grants = Array.isArray(user?.[MANUAL_DRIVER_ACCESS_FIELD])
    ? user[MANUAL_DRIVER_ACCESS_FIELD]
    : [];
  return grants.filter(g => g && g.driverId);
}

/** Driver IDs this user can currently see by manual grant. */
export function getGrantedDriverIds(user, now = new Date()) {
  return getDriverGrants(user)
    .filter(g => isGrantActive(g, now))
    .map(g => g.driverId);
}

/**
 * Whether a driver is visible to this viewer, by company OR by manual grant.
 * Prefer this over isVisibleToCompanies wherever a driver list is scoped.
 */
export function isDriverVisibleTo(driver, viewerCompanyIds, grantedDriverIds = [], viewerTeamIds = []) {
  if (!driver) return false;

  // An explicit, expiring grant from a Super Admin.
  if (grantedDriverIds.includes(driver.id)) return true;

  // Lending: a Super Admin can put a driver into another company's team while
  // the driver's own profile — their company and their BOL branding — stays
  // exactly where it was. The borrowing team must be able to see them, or the
  // team box would list somebody who is invisible on every other screen. That
  // mismatch is what made teams look broken.
  if (driver.teamId && Array.isArray(viewerTeamIds) && viewerTeamIds.includes(driver.teamId)) {
    return true;
  }

  // Otherwise: the company they belong to right now.
  return isVisibleToCompanies(driver, viewerCompanyIds);
}

/**
 * How a viewer should be scoped. Four distinct cases, and they are easy to
 * confuse:
 *
 *   no company field, no grants  -> legacy user, never scoped: sees everything
 *   no company field, has grants -> deliberately curated: sees ONLY the grants
 *   empty array, no grants       -> assigned to nothing on purpose: sees nothing
 *   has companies                -> sees those companies, plus any grants
 *
 * The middle two are why a plain "does the field exist" check isn't enough:
 * granting somebody a driver is an explicit statement of what they should see,
 * and must not fall back to showing them the whole tenant.
 */
/**
 * The three possible answers, named.
 *
 * Two booleans were a trap: `shouldFilter` false means "do not filter", which
 * a screen reads as "show everything" — but it is ALSO false for somebody
 * assigned to no company, who must see nothing. Whether they got everything or
 * nothing came down to whether that screen remembered to check the second flag,
 * and they did not agree. Use `mode`, or better, use scopeDrivers() below.
 */
export const SCOPE_ALL = 'all';          // super admins and legacy unscoped users
export const SCOPE_NONE = 'none';        // deliberately assigned to nothing
export const SCOPE_COMPANIES = 'companies';

export function getViewerScope(user, isSuperAdmin = false) {
  const companyIds = user?.assignedParentCompanyIds || [];
  const grantedDriverIds = getGrantedDriverIds(user);
  const hasCompanyField = user?.[ 'assignedParentCompanyIds' ] !== undefined;

  if (isSuperAdmin) {
    return { mode: SCOPE_ALL, shouldFilter: false, seesNothing: false, companyIds: [], grantedDriverIds, teamIds: [] };
  }

  const shouldFilter = companyIds.length > 0 || grantedDriverIds.length > 0 ||
    viewerTeamIdsOf(user).length > 0;
  const seesNothing = !shouldFilter && hasCompanyField && companyIds.length === 0;

  const mode = shouldFilter
    ? SCOPE_COMPANIES
    : seesNothing
      ? SCOPE_NONE
      : SCOPE_ALL;   // legacy user with no company field at all

  return { mode, shouldFilter, seesNothing, companyIds, grantedDriverIds, teamIds: viewerTeamIdsOf(user) };
}

/**
 * The visible drivers for a viewer. One place, so no screen can invent its own
 * answer — which is how the same user ended up seeing every driver on one page
 * and none on another.
 *
 * Also applies the dispatcher narrowing, so "which drivers may I pick" has a
 * single definition everywhere.
 */
export function scopeDrivers(drivers, scope, user) {
  if (!Array.isArray(drivers)) return [];
  if (scope?.mode === SCOPE_NONE) return [];

  const base = scope?.mode === SCOPE_COMPANIES
    ? drivers.filter(d => isDriverVisibleTo(d, scope.companyIds, scope.grantedDriverIds, scope.teamIds))
    : drivers;

  return limitToLinkedDrivers(base, user, scope?.grantedDriverIds);
}
